import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@avangard/ui/core';

import { PageFiltersDrawer, usePageFilters } from '@modules/shared/organisms';
import { useGeoFilters, useSchoolNumbersFilter } from '@modules/shared/hooks';

import type { RegionEntity, LocationEntity } from '@modules/shared/entities';
import type { SchoolsListFilters } from '@modules/schools/hooks';

const DrawerFilters = (): React.ReactElement => {
    const { t } = useTranslation(['common', 'schools']);

    const {
        filters,
        handleChangeAutocomplete,

        getOptionValue,
        getOptionLabel,
        getOptionSelected,
    } = usePageFilters<SchoolsListFilters<true>>();

    const { regions, regionsLoading, locations, locationsCount, locationsLoading } = useGeoFilters({
        refRegionId: filters.locationRegionId,
    });

    const { schoolNumbersOptions, schoolNumbersLoading } = useSchoolNumbersFilter({
        refRegionId: filters.locationRegionId,
        refLocationId: filters.locationId,
    });

    return (
        <PageFiltersDrawer>
            <Autocomplete<RegionEntity | null, false, false, false>
                fullWidth
                id='locationRegionId'
                label={t('common:forms.labels.region') ?? ''}
                placeholder={t('common:forms.placeholder.choose_region') ?? ''}
                options={regions}
                value={getOptionValue(regions, filters.locationRegionId)}
                disabled={regionsLoading}
                getOptionLabel={getOptionLabel('name')}
                getOptionSelected={getOptionSelected()}
                onChange={handleChangeAutocomplete({ reset: ['locationId'] })}
            />

            <Autocomplete<LocationEntity | null, false, false, false>
                fullWidth
                id='locationId'
                label={t('common:forms.labels.location') ?? ''}
                placeholder={t('common:forms.placeholder.choose_location') ?? ''}
                options={locations}
                value={getOptionValue(locations, filters.locationId)}
                disabled={!filters.locationRegionId || locationsCount === 0 || locationsLoading}
                getOptionLabel={getOptionLabel('name')}
                getOptionSelected={getOptionSelected()}
                onChange={handleChangeAutocomplete()}
            />

            <Autocomplete<AutocompleteOption<number | null | string>, false, false, false>
                fullWidth
                id='number'
                label={t('common:forms.labels.school_number') ?? ''}
                placeholder={t('common:forms.placeholder.choose_school_number') ?? ''}
                options={schoolNumbersOptions}
                value={getOptionValue(schoolNumbersOptions, filters.number, 'key')}
                disabled={schoolNumbersLoading}
                getOptionLabel={getOptionLabel('value')}
                getOptionSelected={getOptionSelected('key')}
                onChange={handleChangeAutocomplete({ extractSingleBy: 'key' })}
            />
        </PageFiltersDrawer>
    );
};

export { DrawerFilters };
